/**
 * Shared design system for every SpecifiC PDF (vector engine).
 *
 * Goal: a single source of truth so the technical report, the prescription
 * report and the visual-layer exports all read as one product family.
 *
 * It is tenant-aware: the palette is derived from the same CSS variables the
 * rest of the app (and the canvas exports) already use, so the PDFs respect
 * each tenant's branding. Everything degrades to sensible defaults outside the
 * browser (e.g. in a headless preview).
 */

export const PDF_PAGE = {
  width: 595,
  height: 842,
  marginX: 40,
  marginBottom: 56,
};

const KAPPA = 0.5522847498307936;

/* ----------------------------------------------------------------------------
 * Color helpers
 * ------------------------------------------------------------------------- */

export const hexToRgb = (color) => {
  const value = String(color || '');

  if (value.startsWith('rgb')) {
    const parts = value.match(/\d+(\.\d+)?/g)?.slice(0, 3).map((part) => Number(part) / 255);
    if (parts?.length === 3) {
      return parts;
    }
  }

  const normalized = value.replace('#', '').padStart(6, '0');
  return [
    parseInt(normalized.slice(0, 2), 16) / 255,
    parseInt(normalized.slice(2, 4), 16) / 255,
    parseInt(normalized.slice(4, 6), 16) / 255,
  ];
};

const toHexChannel = (value) => Math.max(0, Math.min(255, Math.round(value * 255)))
  .toString(16)
  .padStart(2, '0');

/** Mix two colors. amount = 0 -> color, amount = 1 -> target. */
export const mixColors = (color, target, amount) => {
  const a = hexToRgb(color);
  const b = hexToRgb(target);
  const t = Math.max(0, Math.min(1, amount));
  return `#${a.map((channel, index) => toHexChannel(channel + (b[index] - channel) * t)).join('')}`;
};

const rgbOps = (color) => hexToRgb(color).map((value) => value.toFixed(3)).join(' ');

/* ----------------------------------------------------------------------------
 * Tenant-aware palette
 * ------------------------------------------------------------------------- */

const readCssVar = (name, fallback) => {
  try {
    const styles = window.getComputedStyle?.(document.documentElement);
    const value = styles?.getPropertyValue(name)?.trim();
    return value || fallback;
  } catch (error) {
    return fallback;
  }
};

export const getPdfPalette = () => {
  const navy = readCssVar('--kt-header-default-bg-color', '#1d1b2d');
  const primary = readCssVar('--kt-primary', '#1b84ff');
  const ink = readCssVar('--kt-headings-color', '#101426');
  const muted = readCssVar('--kt-text-muted', '#6b7280');

  return {
    navy,
    navyDeep: mixColors(navy, '#000000', 0.18),
    primary,
    primarySoft: mixColors(primary, '#ffffff', 0.9),
    primaryFaint: mixColors(primary, '#ffffff', 0.95),
    onNavy: '#ffffff',
    onNavyEyebrow: mixColors(primary, '#ffffff', 0.55),
    onNavyMuted: mixColors(navy, '#ffffff', 0.72),
    ink,
    slate: '#172033',
    muted,
    line: '#dce4ef',
    lineSoft: '#eef2f7',
    surface: '#ffffff',
    surfaceAlt: '#f4f7fb',
    page: '#ffffff',
  };
};

/* ----------------------------------------------------------------------------
 * Text helpers (shared so wrapping/escaping is identical everywhere)
 * ------------------------------------------------------------------------- */

const cp1252Map = {
  0x20ac: 0x80, 0x201a: 0x82, 0x0192: 0x83, 0x201e: 0x84, 0x2026: 0x85,
  0x2020: 0x86, 0x2021: 0x87, 0x02c6: 0x88, 0x2030: 0x89, 0x0160: 0x8a,
  0x2039: 0x8b, 0x0152: 0x8c, 0x017d: 0x8e, 0x2018: 0x91, 0x2019: 0x92,
  0x201c: 0x93, 0x201d: 0x94, 0x2022: 0x95, 0x2013: 0x96, 0x2014: 0x97,
  0x02dc: 0x98, 0x2122: 0x99, 0x0161: 0x9a, 0x203a: 0x9b, 0x0153: 0x9c,
  0x017e: 0x9e, 0x0178: 0x9f,
};

export const escapePdfText = (value) => {
  const normalized = String(value || '').replace(/â€¢/g, '-');
  return Array.from(normalized).map((char) => {
    const code = char.codePointAt(0);
    const byte = code <= 0xff ? code : cp1252Map[code] || 0x3f;
    return byte.toString(16).padStart(2, '0').toUpperCase();
  }).join('');
};

export const estimateTextWidth = (text, fontSize, bold = false) => {
  const factor = bold ? 0.57 : 0.53;
  return String(text || '').length * fontSize * factor;
};

export const truncateText = (text, maxWidth, fontSize, bold = false) => {
  const value = String(text || '');
  if (estimateTextWidth(value, fontSize, bold) <= maxWidth) {
    return value;
  }
  let output = value;
  while (output.length > 0 && estimateTextWidth(`${output}…`, fontSize, bold) > maxWidth) {
    output = output.slice(0, -1);
  }
  return `${output}…`;
};

export const wrapText = (text, maxWidth, fontSize, bold = false) => {
  const paragraphs = String(text || '').split('\n');
  const lines = [];

  paragraphs.forEach((paragraph) => {
    const words = paragraph.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) {
      lines.push('');
      return;
    }
    let current = '';
    words.forEach((word) => {
      const next = current ? `${current} ${word}` : word;
      if (estimateTextWidth(next, fontSize, bold) <= maxWidth) {
        current = next;
        return;
      }
      if (current) {
        lines.push(current);
      }
      current = '';
      // URLs, scene IDs and compact lineage JSON may contain no whitespace.
      Array.from(word).forEach((character) => {
        if (current && estimateTextWidth(current + character, fontSize, bold) > maxWidth) {
          lines.push(current);
          current = '';
        }
        current += character;
      });
    });
    if (current) {
      lines.push(current);
    }
  });

  return lines;
};

/* ----------------------------------------------------------------------------
 * Low-level painter: stateless drawing bound to a page command sink.
 *
 * `push` receives a single PDF content-stream operator string. The painter
 * works in a top-left origin (PDF's native bottom-left is handled internally),
 * which matches how the report builders already reason about layout.
 * ------------------------------------------------------------------------- */

const roundedRectPath = (left, bottom, width, height, radius) => {
  const r = Math.max(0, Math.min(radius, width / 2, height / 2));
  const x0 = left;
  const y0 = bottom;
  const x1 = left + width;
  const y1 = bottom + height;
  const f = (value) => value.toFixed(2);
  const corner = (fromX, fromY, toX, toY, vertexX, vertexY) =>
    `${f(fromX + (vertexX - fromX) * KAPPA)} ${f(fromY + (vertexY - fromY) * KAPPA)} ` +
    `${f(toX + (vertexX - toX) * KAPPA)} ${f(toY + (vertexY - toY) * KAPPA)} ${f(toX)} ${f(toY)} c`;

  return [
    `${f(x0 + r)} ${f(y0)} m`,
    `${f(x1 - r)} ${f(y0)} l`,
    corner(x1 - r, y0, x1, y0 + r, x1, y0),
    `${f(x1)} ${f(y1 - r)} l`,
    corner(x1, y1 - r, x1 - r, y1, x1, y1),
    `${f(x0 + r)} ${f(y1)} l`,
    corner(x0 + r, y1, x0, y1 - r, x0, y1),
    `${f(x0)} ${f(y0 + r)} l`,
    corner(x0, y0 + r, x0 + r, y0, x0, y0),
    'h',
  ].join(' ');
};

export const createPainter = ({push, palette, pageWidth = PDF_PAGE.width, pageHeight = PDF_PAGE.height}) => {
  const fillRect = (x, top, width, height, color) => {
    push(`q ${rgbOps(color)} rg ${x} ${pageHeight - top - height} ${width} ${height} re f Q`);
  };

  const strokeRect = (x, top, width, height, color, lineWidth = 1) => {
    push(`q ${rgbOps(color)} RG ${lineWidth} w ${x} ${pageHeight - top - height} ${width} ${height} re S Q`);
  };

  const roundedRect = (x, top, width, height, radius, options = {}) => {
    const path = roundedRectPath(x, pageHeight - top - height, width, height, radius);
    const fill = options.fill ? `${rgbOps(options.fill)} rg ` : '';
    const stroke = options.stroke ? `${rgbOps(options.stroke)} RG ${options.lineWidth || 1} w ` : '';
    const paint = options.fill && options.stroke ? 'B' : options.stroke ? 'S' : 'f';
    push(`q ${fill}${stroke}${path} ${paint} Q`);
  };

  const text = (value, x, baselineTop, size = 11, options = {}) => {
    const color = options.color || palette.slate;
    const font = options.bold ? 'F2' : 'F1';
    const display = options.maxWidth ? truncateText(value, options.maxWidth, size, options.bold) : value;
    // Tc survives BT/ET, so every text run must reset it instead of inheriting a header's spacing.
    const charSpace = ` ${options.letterSpacing ?? 0} Tc`;
    push(
      `BT /${font} ${size} Tf${charSpace} ${rgbOps(color)} rg 1 0 0 1 ${x} ${pageHeight - baselineTop} Tm <${escapePdfText(display)}> Tj ET`
    );
  };

  const hline = (x1, top, x2, color, lineWidth = 0.8) => {
    push(`q ${rgbOps(color)} RG ${lineWidth} w ${x1} ${pageHeight - top} m ${x2} ${pageHeight - top} l S Q`);
  };

  const image = (name, x, top, width, height) => {
    push(`q ${width} 0 0 ${height} ${x} ${pageHeight - top - height} cm /${name} Do Q`);
  };

  return {fillRect, strokeRect, roundedRect, text, hline, image, palette, pageWidth, pageHeight};
};

/* ----------------------------------------------------------------------------
 * High-level components (the parts that make every PDF look like family)
 * ------------------------------------------------------------------------- */

const HEADER = {
  inset: 28,
  height: 122,
  radius: 16,
};

/**
 * Branded header band. `drawLogo` (optional) is called with
 * (painter, {x, top, maxWidth, maxHeight}) so each report can place its own
 * registered logo image; when absent the SpecifiC wordmark is used.
 * Returns the Y just below the band.
 */
export const drawHeaderBand = (painter, {
  eyebrow,
  title,
  metaLabel,
  metaValue,
  drawLogo,
  brandText = 'SpecifiC',
  metaRightPadding = 42,
  wrapTitle = false,
}) => {
  const {palette, pageWidth} = painter;
  const x = HEADER.inset;
  const width = pageWidth - HEADER.inset * 2;
  const top = HEADER.inset;
  const titleLines = wrapTitle ? wrapText(title, width - 56, 19, true) : [title];
  const extraTitleHeight = (titleLines.length - 1) * 23;

  painter.roundedRect(x, top, width, HEADER.height + extraTitleHeight, HEADER.radius, {fill: palette.navy});

  const contentX = x + 28;

  if (drawLogo) {
    drawLogo(painter, {x: contentX, top: top + 22, maxWidth: 150, maxHeight: 30});
  } else {
    painter.text(brandText, contentX, top + 42, 22, {bold: true, color: palette.onNavy});
  }

  if (eyebrow) {
    painter.text(String(eyebrow).toUpperCase(), contentX, top + 70, 8.5, {
      bold: true,
      color: palette.onNavyEyebrow,
      letterSpacing: 1.4,
    });
  }

  titleLines.forEach((line, index) => {
    painter.text(line, contentX, top + 92 + index * 23, 19, {
      bold: true,
      color: palette.onNavy,
      maxWidth: width - 56,
    });
  });
  painter.roundedRect(contentX, top + 104 + extraTitleHeight, 46, 3, 1.5, {fill: palette.primary});

  if (metaLabel || metaValue) {
    const metaText = [metaLabel, metaValue].filter(Boolean).join(': ');
    painter.text(metaText, x + width - metaRightPadding - estimateTextWidth(metaText, 8.5), top + 36, 8.5, {
      color: palette.onNavyMuted,
    });
  }

  return top + HEADER.height + extraTitleHeight + 26;
};

/** Row of metric chips. metrics: [{label, value, width}]. Returns next Y. */
export const drawMetricChips = (painter, top, metrics, options = {}) => {
  const {palette} = painter;
  const x0 = options.x || PDF_PAGE.marginX;
  const gap = options.gap || 9;
  const height = options.height || 52;
  let x = x0;

  metrics.forEach(({label, value, width, valueSize: fixedValueSize}) => {
    painter.roundedRect(x, top, width, height, 10, {fill: palette.surfaceAlt});
    painter.roundedRect(x, top + 12, 3, height - 24, 1.5, {fill: palette.primary});
    painter.text(String(label).toUpperCase(), x + 14, top + 19, 7.5, {
      bold: true,
      color: palette.muted,
      letterSpacing: 0.6,
      maxWidth: width - 22,
    });
    const valueText = String(value);
    const valueSize = fixedValueSize || (estimateTextWidth(valueText, 13, true) > width - 24 ? 10 : 13);
    painter.text(valueText, x + 14, top + 38, valueSize, {
      bold: true,
      color: palette.ink,
      maxWidth: width - 22,
    });
    x += width + gap;
  });

  return top + height + 24;
};

/** Section header: accent tick + title + hairline. Returns next Y. */
export const drawSectionHeader = (painter, top, title, options = {}) => {
  const {palette, pageWidth} = painter;
  const x = options.x || PDF_PAGE.marginX;
  const right = pageWidth - (options.x || PDF_PAGE.marginX);
  const y = top + 4;

  painter.roundedRect(x, y - 9, 3.5, 14, 1.75, {fill: palette.primary});
  painter.text(title, x + 12, y + 2, 12.5, {bold: true, color: palette.ink});
  painter.hline(x, y + 13, right, palette.line, 0.8);

  return y + 28;
};

/** Footer: hairline accent rule + page label. */
export const drawFooter = (painter, {index, total, brandText = 'SpecifiC'}) => {
  const {palette, pageWidth, pageHeight} = painter;
  const y = pageHeight - 34;

  painter.hline(PDF_PAGE.marginX, y - 8, pageWidth - PDF_PAGE.marginX, palette.line, 0.8);
  painter.roundedRect(PDF_PAGE.marginX, y - 4, 22, 3, 1.5, {fill: palette.primary});
  painter.text(brandText, PDF_PAGE.marginX + 30, y + 2, 8, {bold: true, color: palette.muted});

  const pageText = `${index + 1} / ${total}`;
  painter.text(pageText, pageWidth - PDF_PAGE.marginX - estimateTextWidth(pageText, 8.5), y + 2, 8.5, {
    color: palette.muted,
  });
};
